import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAvailability } from '../hooks/useAvailability';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { api } from '../services/api';
import AvailabilityGrid from '../components/AvailabilityGrid';
import ProgressBar from '../components/ProgressBar';
import Button from '../components/ui/Button';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

/**
 * Start page for setting availability without login requirement
 * Supports both group classes and private tutoring trials
 */
const StartPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const { classTypeId } = useParams();
  
  // Read URL parameters for pre-selection
  const searchParams = new URLSearchParams(routerLocation.search);
  const urlParams = {
    exam: searchParams.get('exam'), // e.g., ?exam=IB
    goal: searchParams.get('goal'), // e.g., ?goal=hsk
    level: searchParams.get('level') // e.g., ?level=4 or ?level=Beginner
  };
  
  // State for class type data
  const [classType, setClassType] = useState(null);
  useDocumentTitle(classType?.name ? `${classType.name} - Hong Kong` : 'Hong Kong');
  const [loadingClassType, setLoadingClassType] = useState(false);
  const [classTypeError, setClassTypeError] = useState(null);
  
  // State for heatmap data
  const [heatmapData, setHeatmapData] = useState({});
  const [loadingHeatmap, setLoadingHeatmap] = useState(false);

  // Determine if this is a tutoring trial
  const isTutoringTrial = classTypeId?.includes('tutoring-trial');
  
  // Determine if this is IB Chinese classes
  const isIBChinese = classTypeId === 'ib-chinese-classes';
  
  // State for class-specific question
  const [questionResponse, setQuestionResponse] = useState('');
  
  // Use availability hook
  const { 
    availability, 
    setAvailability,
    loading,
    error,
    hasSelections
  } = useAvailability();
  
  // Load availability from routerLocation state if coming back from registration
  useEffect(() => {
    if (routerLocation.state?.availability) {
      setAvailability(routerLocation.state.availability);
    }
    if (routerLocation.state?.questionResponse) {
      setQuestionResponse(routerLocation.state.questionResponse);
    }
  }, [routerLocation.state, setAvailability]);
  
  // Load class type and heatmap independently so the grid is interactive immediately
  useEffect(() => {
    if (!classTypeId) {
      setClassTypeError('No course selected');
      return;
    }

    sessionStorage.setItem('startClassTypeId', classTypeId);
    setLoadingClassType(true);
    setClassTypeError(null);

    api.classTypes.getById(classTypeId)
      .then((result) => {
        if (result) {
          setClassType(result);
        } else {
          setClassTypeError('Course not found');
        }
      })
      .catch((err) => {
        console.error('Error loading course information:', err);
        setClassTypeError('Failed to load course information');
      })
      .finally(() => setLoadingClassType(false));
  }, [classTypeId]);

  useEffect(() => {
    if (!classTypeId || isTutoringTrial) return;

    const cacheKey = `heatmap_${classTypeId}`;
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      const parsedCache = JSON.parse(cached);
      if (Date.now() - parsedCache.timestamp < 300000) {
        setHeatmapData(parsedCache.data);
        return;
      }
    }

    setLoadingHeatmap(true);
    api.availability.getHeatmap(classTypeId)
      .then((heatmap) => {
        if (heatmap) {
          sessionStorage.setItem(cacheKey, JSON.stringify({ data: heatmap, timestamp: Date.now() }));
          setHeatmapData(heatmap);
        }
      })
      .catch((err) => console.error('Error loading heatmap:', err))
      .finally(() => setLoadingHeatmap(false));
  }, [classTypeId, isTutoringTrial]);
  
  // Handle availability change
  const handleAvailabilityChange = (newAvailability) => {
    setAvailability(newAvailability);
  };
  
  // Handle reset
  const handleReset = () => {
    setAvailability({});
    setQuestionResponse('');
  };
  
  // Handle submit
  const handleSubmit = () => {
    if (!hasSelections) {
      alert('Please select at least one time slot');
      return;
    }
    
    // Validate question response if required
    if (!questionResponse.trim()) {
      const questionText = isIBChinese 
        ? 'Please answer the question about your child\'s IB programme'
        : 'Please tell us about your level and background in Chinese';
      alert(questionText);
      return;
    }
    
    // Clear heatmap cache since data will change after submission
    if (classTypeId && !isTutoringTrial) {
      const cacheKey = `heatmap_${classTypeId}`;
      sessionStorage.removeItem(cacheKey);
    }
    
    if (!user) {
      // User not logged in - redirect to start-specific registration with availability data
      navigate('/start-register', {
        state: {
          returnTo: '/start-confirmation',
          returnState: {
            classType: classType?.category || classType?.name?.toLowerCase() || 'general',
            availability,
            isTutoringTrial,
            urlParams,
            questionResponse
          }
        }
      });
      return;
    }
    
    // User is logged in - go directly to confirmation
    navigate('/start-confirmation', {
      state: {
        returnState: {
          classType: classType?.category || classType?.name?.toLowerCase() || 'general',
          availability,
          isTutoringTrial,
          urlParams,
          questionResponse
        }
      }
    });
  };
  
  // Show error state if no classTypeId or class type not found (AFTER loading completes)
  if (!classTypeId || classTypeError) {
    return (
      <div className="min-h-screen bg-black text-white">
        <main className="container mx-auto py-8 px-4">
          <div className="max-w-md mx-auto text-center">
            <div className="mb-8">
              <h2 className="text-2xl font-bold mb-4">Course Selection Required</h2>
              <p className="text-gray-400 mb-6">
                {classTypeError || 'A course needs to be chosen first before setting your availability.'}
              </p>
              <Button
                variant="primary"
                onClick={() => navigate('/classes')}
                className="bg-purple-600 hover:bg-purple-700"
              >
                Browse Available Classes
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-black text-white">
      <Navigation />
      {/* Main content */}
      <main className="container mx-auto py-8 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Title */}
          <div className="text-center mb-8">
            {/* Class name label */}
            <div className="mb-3">
              {loadingClassType ? (
                <div className="inline-block h-7 w-36 bg-gray-700 rounded-full animate-pulse"></div>
              ) : classType ? (
                <span className="inline-block px-4 py-1.5 bg-purple-900 bg-opacity-30 border border-purple-700 rounded-full text-purple-300 text-sm font-medium">
                  {classType.name}
                </span>
              ) : null}
            </div>
            
            <h2 className="text-2xl font-bold">
              {isTutoringTrial 
                ? "Schedule Your Trial Lesson Now" 
                : "Schedule Your Class Now"}
            </h2>
          </div>
          
          {/* Progress bar */}
          <ProgressBar currentStep={1} />
          
          {error && (
            <div className="p-4 bg-red-900 bg-opacity-20 border border-red-700 rounded-lg mb-6">
              <p className="text-red-400">{error}</p>
            </div>
          )}
          
          {/* Availability grid */}
          <div className="bg-gray-800 rounded-lg border border-gray-700 shadow-lg overflow-hidden mb-8">
            <div className="bg-gradient-to-r from-gray-800 to-gray-700 px-6 py-4 border-b border-gray-700">
              <h3 className="font-semibold text-lg">Tap what times you'd be free</h3>
              
              {isTutoringTrial ? (
                <p>We'll message you to confirm one of your chosen times that work for both you and your tutor.</p>
              ) : (
                <p>We'll contact you once at least two students are available at your chosen times to form a class.</p>
              )}
              
              {!isTutoringTrial && (
                loadingHeatmap ? (
                  <p className="text-sm text-gray-500 mt-2">Loading availability data...</p>
                ) : Object.keys(heatmapData).length > 0 ? (
                  <p className="text-sm text-blue-400 mt-2">
                    🔵 Blue highlights show times when others are also looking for classmates
                  </p>
                ) : null
              )}
            </div>
            <div className="p-6">
              <AvailabilityGrid
                availability={availability}
                onChange={handleAvailabilityChange}
                classType={classType?.category || classType?.name?.toLowerCase() || 'general'}
                heatmapData={isTutoringTrial ? {} : heatmapData}
                showHeatmap={!isTutoringTrial}
              />
            </div>
            
            {/* Class-specific question */}
            <div className="px-6 pb-6 border-t border-gray-700 pt-6">
              <label className="block mb-3">
                <span className="text-sm font-medium text-gray-200">
                  {isIBChinese 
                    ? "What IB programme and year is your child? Is Chinese your child's first language?"
                    : "What is your level and background in Chinese (Mandarin or Cantonese)?"}
                </span>
                <span className="text-red-400 ml-1">*</span>
              </label>
              <textarea
                value={questionResponse}
                onChange={(e) => setQuestionResponse(e.target.value)}
                placeholder={isIBChinese 
                  ? "e.g., IB DP Year 1, Chinese is their first language"
                  : "e.g., Beginner in Mandarin, native Cantonese speaker"}
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                rows="3"
              />
            </div>
          </div>
          
          {/* Action buttons */}
          <div className="flex justify-between">
            <Button
              variant="secondary"
              onClick={handleReset}
              disabled={!hasSelections && !questionResponse}
              className="bg-gray-700 hover:bg-gray-600 border border-gray-600"
            >
              Reset Selections
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={!hasSelections || loading || !questionResponse.trim()}
              isLoading={loading}
              className="bg-purple-600 hover:bg-purple-700"
            >
              Continue
            </Button>
          </div>
        </div>
      </main>

      <Footer />

      <div className="h-20 md:hidden" aria-hidden="true"></div>

      {/* Sticky Mobile Contact Bar */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-gray-900 border-t border-gray-700 shadow-lg z-50">
        {/* Questions header */}
        <div className="text-center pt-2 pb-1">
          <p className="text-gray-400 text-xs">Questions?</p>
        </div>
        
        <div className="px-4 pb-3 flex items-center justify-center gap-3">
          <a
            href="https://wa.me/85251776613"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-700 rounded-lg transition-colors flex-1"
          >
            <svg className="w-5 h-5 flex-shrink-0 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
            </svg>
            <span className="text-sm font-semibold text-white">WhatsApp</span>
          </a>
          
          <a
            href="mailto:aileen@mandarintutorhk.com"
            className="flex items-center justify-center gap-2 px-4 py-2.5 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors flex-1"
          >
            <svg className="w-5 h-5 flex-shrink-0 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <span className="text-sm font-semibold text-white">Email</span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default StartPage;